import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ArrowLeftRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  DEFAULT_RIBBON_CONFIG,
  detectAudience,
  pickRibbonVariant,
  setManualAudience,
  trackRibbon,
  type RibbonAudience,
  type RibbonConfig,
  type RibbonVariant,
} from "@/components/marketing/ribbonVariants";

const RIBBON_DISMISS_KEY = "sb_trial_ribbon_dismissed";
const VISITED_SENTINEL_KEY = "sb_landing_visited_at";

// Module-level guard so React 18 StrictMode's double-mount doesn't flip the
// "first visit" state twice and accidentally show the ribbon on visit #1.
let __ribbonEffectRan = false;

interface Props { surface: "landing" | "pricing" }

/**
 * TrialRibbon — audience-aware, admin-editable notification strip that
 * shows returning visitors a short nudge tailored to their intent:
 *
 *   • Shoppers (buyer) get shopping-flavored copy — never the word "trial"
 *     — so they don't mistake SociBaba for a SaaS they must sign up for.
 *   • Brands / creators (seller) still see the 14-day-trial pitch.
 *
 * A tiny "Are you a brand? / Just shopping?" toggle lets a visitor flip
 * modes when auto-detection guessed wrong. Copy for every variant is
 * fetched from `/api/marketing/ribbon-config` so the admin console can
 * tweak it live without a code deploy.
 */
const TrialRibbon: React.FC<Props> = ({ surface }) => {
  const [visible, setVisible] = useState(false);
  const [manualAud, setManualAud] = useState<RibbonAudience | null>(null);

  // Fetch admin-editable ribbon config. Public endpoint, so no auth. If
  // the request fails we still render — the hook returns shipped defaults.
  const { data: config } = useQuery<RibbonConfig>({
    queryKey: ["marketing", "ribbon-config"],
    queryFn: async () => {
      const { data } = await api.get("/marketing/ribbon-config");
      return data as RibbonConfig;
    },
    initialData: DEFAULT_RIBBON_CONFIG,
    staleTime: 5 * 60 * 1000, // 5 min — copy rarely changes mid-session
    retry: 1,
  });

  const audience: RibbonAudience = useMemo(() => {
    if (manualAud) return manualAud;
    return detectAudience(config?.default_audience || "buyer");
  }, [manualAud, config?.default_audience]);

  const pool = audience === "seller"
    ? (config?.seller_variants || [])
    : (config?.buyer_variants || []);

  const variant: RibbonVariant = useMemo(() => {
    return pickRibbonVariant(audience, pool);
    // Recompute when the pool identity or the audience changes.
  }, [audience, pool]);

  useEffect(() => {
    if (__ribbonEffectRan) return;
    __ribbonEffectRan = true;
    try {
      const dismissed = localStorage.getItem(RIBBON_DISMISS_KEY) === "1";
      if (dismissed) return;
      const previousVisit = localStorage.getItem(VISITED_SENTINEL_KEY);
      if (!previousVisit) {
        localStorage.setItem(VISITED_SENTINEL_KEY, String(Date.now()));
        return;
      }
      setVisible(true);
    } catch { /* ignore */ }
  }, []);

  // Fire the "shown" event whenever variant OR audience changes so we
  // capture manual switches too.
  useEffect(() => {
    if (visible && variant) {
      trackRibbon("trial_ribbon_shown", variant.id, surface, audience);
    }
  }, [visible, variant?.id, surface, audience]);

  if (!visible || !config?.enabled) return null;

  const dismiss = () => {
    try { localStorage.setItem(RIBBON_DISMISS_KEY, "1"); } catch { /* ignore */ }
    trackRibbon("trial_ribbon_dismissed", variant.id, surface, audience);
    setVisible(false);
  };

  const toggleAudience = () => {
    const next: RibbonAudience = audience === "buyer" ? "seller" : "buyer";
    setManualAudience(next);
    setManualAud(next);
    trackRibbon("trial_ribbon_audience_flipped", variant.id, surface, next);
  };

  const flipLabel =
    audience === "buyer" ? "Are you a brand?" : "Just shopping?";

  return (
    <div
      data-testid="landing-trial-ribbon"
      data-variant={variant.id}
      data-audience={audience}
      data-surface={surface}
      className="w-full bg-[color:var(--sb-navy)] text-[color:var(--sb-cream)] text-xs sm:text-sm"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-3.5 w-3.5 text-[color:var(--sb-saffron)] shrink-0" strokeWidth={2} />
          <span className="truncate">
            {variant.body}{" "}
            <span className="font-semibold text-[color:var(--sb-cream)]">{variant.emphasis}</span>
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Link
            to={variant.href}
            data-testid="landing-trial-ribbon-cta"
            onClick={() => trackRibbon("trial_ribbon_cta_click", variant.id, surface, audience)}
            className="text-[color:var(--sb-cream)] underline underline-offset-4 decoration-[color:var(--sb-saffron)]/70 hover:decoration-[color:var(--sb-saffron)] hidden sm:inline font-semibold"
          >
            {variant.cta}
          </Link>
          <button
            type="button"
            data-testid="landing-trial-ribbon-audience-flip"
            onClick={toggleAudience}
            className="hidden md:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/60 hover:text-white transition-colors ml-3 px-2 py-1 rounded-full border border-white/10 hover:border-white/30"
            aria-label={`Switch ribbon audience: ${flipLabel}`}
          >
            <ArrowLeftRight className="h-3 w-3" />
            {flipLabel}
          </button>
          <button
            data-testid="landing-trial-ribbon-dismiss"
            onClick={dismiss}
            aria-label="Dismiss trial ribbon"
            className="ml-1 h-7 w-7 rounded-full inline-flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialRibbon;
